if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.windowSize = window.innerWidth;
        this.windowSizeScrollUpdate = 750;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        document.addEventListener('click', this.onDocumentClick.bind(this));
      }

      connectedCallback() {
        this.moveViewerOutsideGalleryOnDesktop();
      }

      moveViewerOutsideGalleryOnDesktop() {
        if (!this.elements?.viewer) return;
        if (!window.matchMedia('(min-width: 991px)').matches) return;
        if (!this.parentElement) return;
        if (this.elements.viewer.parentElement !== this) return;

        this.parentElement.insertBefore(this.elements.viewer, this.nextSibling);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement?.dataset?.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.toggleInactiveVideoMedia(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend, disableAnimation = false) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const mediaList = activeMedia?.parentElement;
        let previousScrollBehavior;

        if (disableAnimation && mediaList) {
          previousScrollBehavior = mediaList.style.scrollBehavior;
          mediaList.style.scrollBehavior = 'auto';
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            mediaList.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: disableAnimation ? 'auto' : 'smooth' });
          }
          if (disableAnimation && mediaList) {
            requestAnimationFrame(() => {
              mediaList.style.scrollBehavior = previousScrollBehavior;
            });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);

        //to hide products tags in PDP on infographic image
        if (mediaId.includes('infographic-pdp-img')) {
          document.querySelector('.product__gallery-slider').classList.add('hide-gallery-tags')
        }
        else {
          document.querySelector('.product__gallery-slider').classList.remove('hide-gallery-tags')
        }

        // Desktop popup mode
        if (window.matchMedia('(min-width: 991px)').matches) {
          this.elements.viewer.classList.add('popup-opened');
          this.lockBodyScroll();
          this.pauseThumbnailVideos();
        }
      }

      pauseThumbnailVideos() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('video').forEach((video) => video.pause());
      }

      resumeThumbnailVideos() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('video').forEach((video) => {
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => { });
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.windowSize > this.windowSizeScrollUpdate) {
          if (this.elements.thumbnails.isSlideVisibleY(thumbnail, 10)) return;
        } else {
          if (this.elements.thumbnails.isSlideVisibleX(thumbnail, 10)) return;
        }

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      onDocumentClick(event) {
        if (!this.elements?.viewer) return;
        if (!this.elements.viewer.classList.contains('popup-opened')) return;
        if (document.querySelector('.image-magnify-full-size')) return;

        const target = event.target;
        if (target.closest('.product-media-container')) return;
        if (this.elements.thumbnails && this.elements.thumbnails.contains(target)) return;
        if (target.closest('.slider-button--prev, .slider-button--next')) return;

        this.elements.viewer.classList.remove('popup-opened');
        this.unlockBodyScroll();
        this.resumeThumbnailVideos();
        this.muteActiveMedia();
      }

      lockBodyScroll() {
        this.scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.classList.add('scroll-lock');
      }

      unlockBodyScroll() {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        // Restore the scroll position instantly. The root element has
        // `scroll-behavior: smooth`, which would otherwise animate this jump
        // and look like the page scrolls from the top down to the opened image.
        const rootEl = document.documentElement;
        const previousScrollBehavior = rootEl.style.scrollBehavior;
        rootEl.style.scrollBehavior = 'auto';
        window.scrollTo(0, this.scrollY || 0);
        rootEl.style.scrollBehavior = previousScrollBehavior;
        document.body.classList.remove('scroll-lock');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const hasPlayableMedia = !!activeItem.querySelector('video, iframe, model-viewer, .deferred-media');
        if (!hasPlayableMedia) {
          window.pauseAllMedia({ resumeThumbnailVideos: false });
        }
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        if (hasPlayableMedia) {
          const videos = activeItem.querySelectorAll('video');
          videos.forEach((video) => {
            let playIcon = video.parentElement.querySelector('.product-thumbnail-play-icon');
            if (!video.ended) {
              video.play().catch(() => { });
            }
            if (playIcon) {
              playIcon.src = 'https://cdn.shopify.com/s/files/1/0646/3304/5218/files/pause-icon-hp.svg?v=1761653355';
            }
          });
        }
      }

      muteActiveMedia() {
        const videos = this.elements.viewer.querySelectorAll('video');
        if (videos.length > 0) {
          videos.forEach((video) => {
            let muteIcon = video.parentElement.querySelector('.product-thumbnail-mute-icon');
            if (!video.muted) {
              video.muted = true;
              muteIcon.src = 'https://cdn.shopify.com/s/files/1/0646/3304/5218/files/mute-icon.svg?v=1761653354';
            }
          });
        }
      }

      toggleInactiveVideoMedia(currentEl) {
        const videos = this.elements.viewer.querySelectorAll('video');
        if (currentEl && videos.length > 0) {
          if (currentEl.querySelector('video')) {
            currentEl.querySelector('video').play();
            currentEl.querySelector('.product-thumbnail-play-icon').src = 'https://cdn.shopify.com/s/files/1/0646/3304/5218/files/pause-icon-hp.svg?v=1761653355';
          }
          else {
            videos.forEach((video) => {
              video.pause();
            });
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}